import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Grid, Typography, CircularProgress, Alert, TextField,
    MenuItem, Select, FormControl, InputLabel, Chip, Avatar,
    Card, CardMedia, CardContent, CardActions, Button, Pagination,
    Paper
} from '@mui/material';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import { InfluencerStorefrontService } from '../../../../core/services/influencer-storefront.service';
import { SearchRounded } from '@mui/icons-material';
import useBodyClass from '../../../../hooks/useBodyClass';
import { PublicPageUnavailable } from '../../../common/public-page-unavailable/PublicPageUnavailable';

const CURRENCY_SYMBOLS: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'د.إ' };
const PAGE_SIZE = 24;

// First-party anonymous ids for view analytics (no PII).
const getVisitorId = (): string => {
    try {
        let id = localStorage.getItem('cc_sf_vid');
        if (!id) { id = 'v_' + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('cc_sf_vid', id); }
        return id;
    } catch { return 'v_anon'; }
};
const getSessionId = (): string => {
    try {
        let id = sessionStorage.getItem('cc_sf_sid');
        if (!id) { id = 's_' + Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem('cc_sf_sid', id); }
        return id;
    } catch { return 's_anon'; }
};

export const PublicInfluencerStorefront: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [storefront, setStorefront] = useState<any>(null);
    const [allProducts, setAllProducts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [category, setCategory] = useState('');
    const [page, setPage] = useState(1);
    useBodyClass('public-store-front')
    useEffect(() => {
        if (!slug) return;
        InfluencerStorefrontService.getPublicStorefront(slug)
            .then((res: any) => {
                if (res?.status === 1 && res?.data?.storefront) {
                    setStorefront(res.data.storefront);
                    setAllProducts(res.data.products || []);
                    // Fire the impression beacon once the store resolves.
                    InfluencerStorefrontService.trackView({ slug, visitor_id: getVisitorId(), session_id: getSessionId() });
                } else {
                    setError('Storefront not found');
                }
            })
            .catch(() => setError('Failed to load storefront'))
            .finally(() => setLoading(false));
    }, [slug]);

    const filtered = useMemo(() => {
        let list = allProducts;
        if (category) list = list.filter(p => p.category === category);
        if (search) list = list.filter(p => (p.product_title || '').toLowerCase().includes(search.toLowerCase()));
        return list;
    }, [allProducts, category, search]);

    useEffect(() => { setPage(1); }, [search, category]);

    const categories = useMemo(() => [...new Set(allProducts.map(p => p.category).filter(Boolean))], [allProducts]);
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    const sym = (c: string) => CURRENCY_SYMBOLS[c] || c;

    if (loading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;
    // OLD: if (error) return <Box p={4}><Alert severity="error">{error}</Alert></Box>;
    // The API now also returns "not found" when the owner's plan no longer
    // includes the storefront, so this branch is what an expired store shows —
    // a red error alert would read as a site fault rather than a gone page.
    if (error) return <PublicPageUnavailable variant="storefront" />;


    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100vh",
                }}
            >
                <Box

                    sx={{
                        position: "relative",
                        mb: { xs: 9, md: 10 },
                    }}
                >
                    <Box
                        sx={{
                            height: {
                                xs: 190,
                                md: 210,
                            },
                            position: "relative",
                            overflow: "hidden",
                            // A cover image is optional now that first-run setup can skip
                            // it, so the band paints its own gradient underneath. Without
                            // this an <img src=""> drew a broken-image glyph.
                            background: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 55%, #c4b5fd 100%)",
                        }}
                    >
                        {/* OLD: rendered unconditionally, even with no banner saved.
                            <img src={storefront.store_banner} ... /> */}
                        {!!storefront.store_banner && (
                            <img
                                src={storefront.store_banner}
                                alt=""
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                }}
                            />
                        )}

                        {/* Dark overlay */}
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                background:
                                    "linear-gradient(to bottom, rgba(0,0,0,.05), rgba(0,0,0,.25))",
                            }}
                        />
                    </Box>

                    {/* Banner */}
                    {/* {storefront.store_banner && (
                        <Box
                            sx={{
                                height: {
                                    xs: 220,
                                    md: 320,
                                },
                                overflow: "hidden",
                                position: "relative",
                                borderRadius: {
                                    xs: 0,
                                    md: 0,
                                },
                            }}
                        >

                            <img
                                src={
                                    storefront.store_banner
                                }
                                alt=""
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                }}
                            />
                        </Box>
                    )} */}
                    <Paper
                        elevation={0}
                        sx={{
                            position: { xs: "relative", md: "relative" },
                            left: "50%",
                            marginTop:{ xs: "-120px", md: '-160px'},
                            transform: "translateX(-50%)",

                            width: {
                                xs: "92%",
                                md: 640,
                            },

                            borderRadius: "20px",

                            px: { xs: 3, md: 5 },
                            py: { xs: 3, md: 4 },

                            textAlign: "center",

                            border: "1px solid #E5E7EB",
                            background: "#fff",

                            boxShadow:
                                "0 8px 24px rgba(15,23,42,.08)",
                        }}
                    >

                        <Avatar
                            src={storefront.store_logo}
                            sx={{
                                width: 96,
                                height: 96,

                                margin: "auto",
                                mt: -8,

                                border: "5px solid #fff",

                                boxShadow:
                                    "0 10px 24px rgba(15,23,42,.15)",

                                bgcolor: storefront.store_logo ? "#fff" : "#f5f3ff",
                                color: "#7c3aed",
                                fontSize: 34,
                                fontWeight: 700,
                            }}
                        >
                            {/* No logo uploaded yet -> the shop's initial, not MUI's
                                default person silhouette (this is a shop, not a person). */}
                            {(storefront.store_name || "?").trim().charAt(0).toUpperCase()}
                        </Avatar>

                        <Typography
                            variant="h5"
                            fontWeight={700}
                            mt={2}
                        >
                            {storefront.store_name}
                        </Typography>

                        {!!storefront.store_description && (

                            <Typography
                                sx={{
                                    mt: 1,
                                    color: "#64748B",
                                    fontSize: 14,
                                    maxWidth: 480,
                                    mx: "auto",
                                    lineHeight: 1.6,
                                }}
                            >
                                {storefront.store_description}
                            </Typography>

                        )}
                        <Box
                            mt={2.5}
                            display="flex"
                            justifyContent="center"
                            gap={1}
                            flexWrap="wrap"
                        >
                            <Chip
                                label={`${allProducts.length} Products`}
                                sx={{
                                    px: 1,
                                    height: 32,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    borderRadius: "999px",
                                    bgcolor: "#EEF2FF",
                                    color: "#4F46E5",
                                }}
                            />

                            <Chip
                                label="Verified Store"
                                sx={{
                                    px: 1,
                                    height: 32,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    borderRadius: "999px",
                                    bgcolor: "#ECFDF5",
                                    color: "#059669",
                                }}
                            />
                        </Box>

                    </Paper>
                </Box>

                {/* Store Card */}

                <Box flex={1} sx={{padding:'0 20px'}}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: "16px",
                            border: "1px solid #E5E7EB",
                            bgcolor: "#fff",
                            boxShadow: "0 1px 3px rgba(15,23,42,.06)",
                            maxWidth: '1400px',
                            mx: 'auto'

                        }}
                    >

                        <Grid
                            container
                            spacing={2}
                            alignItems="center"
                        >

                            {/* Search */}

                            <Grid item xs={12} md={6}>

                                <TextField
                                    fullWidth
                                    placeholder="Search products..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <SearchRounded
                                                sx={{
                                                    mr: 1,
                                                    color: "#94A3B8",
                                                }}
                                            />
                                        ),
                                    }}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            bgcolor: "#F8FAFC",
                                            borderRadius: "10px",
                                            "& fieldset": { borderColor: "#E5E7EB" },
                                        },
                                    }}
                                />

                            </Grid>

                            {/* Category */}

                            <Grid item xs={12} sm={6} md={3}>

                                <FormControl fullWidth>

                                    <Select
                                        value={category}
                                        displayEmpty
                                        onChange={(e) =>
                                            setCategory(e.target.value)
                                        }
                                        sx={{
                                            bgcolor: "#F8FAFC",
                                            borderRadius: "10px",
                                            "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB" },
                                        }}
                                    >

                                        <MenuItem value="">
                                            All Categories
                                        </MenuItem>

                                        {categories.map((c) => (

                                            <MenuItem
                                                key={c}
                                                value={c}
                                            >
                                                {c}
                                            </MenuItem>

                                        ))}

                                    </Select>

                                </FormControl>

                            </Grid>

                            {/* Product Count */}

                            <Grid item xs={12} sm={6} md={3}>

                                <Box
                                    display="flex"
                                    justifyContent={{
                                        xs: "flex-start",
                                        md: "flex-end",
                                    }}
                                >

                                    <Chip
                                        label={`${filtered.length} Products`}
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: 13,
                                            px: 1,
                                            height: 32,
                                            borderRadius: "999px",
                                            bgcolor: "#EEF2FF",
                                            color: "#4F46E5",
                                        }}
                                    />

                                </Box>

                            </Grid>

                        </Grid>
                        <Box
                            mt={3}
                            display="flex"
                            gap={1}
                            flexWrap="wrap"
                        >

                            <Chip
                                label="All"
                                clickable
                                onClick={() => setCategory("")}
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 13,
                                    borderRadius: "999px",
                                    bgcolor: !category ? "#000" : "#F1F5F9",
                                    color: !category ? "#fff" : "#475569",
                                    "&:hover": {
                                        bgcolor: !category ? "#111" : "#E2E8F0",
                                    },
                                }}
                            />

                            {categories.map((cat) => (

                                <Chip
                                    key={cat}
                                    label={cat}
                                    clickable
                                    onClick={() => setCategory(cat)}
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: 13,
                                        borderRadius: "999px",
                                        bgcolor: category === cat ? "#000" : "#F1F5F9",
                                        color: category === cat ? "#fff" : "#475569",
                                        "&:hover": {
                                            bgcolor: category === cat ? "#111" : "#E2E8F0",
                                        },
                                    }}
                                />

                            ))}

                        </Box>
                    </Paper>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ maxWidth: '1400px', margin: '0 auto 24px', borderBottom: '1px solid #E5E7EB', paddingBottom: '16px' }}
                    >
                        <Typography
                            variant="h5"
                            fontWeight={700}
                        >
                            Featured Products
                        </Typography>

                        <Chip
                            label={`${filtered.length} Products`}
                            sx={{
                                fontWeight: 600,
                                fontSize: 13,
                                borderRadius: "999px",
                                bgcolor: "#EEF2FF",
                                color: "#4F46E5",
                            }}
                        />
                    </Box>

                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: "1400px",
                            gap:'1rem',
                            px: {
                                xs: 2,
                                sm: 3,
                                md: 5,
                                lg: 0,
                            },
                            mx:'auto',
                            pb: 8,
                        }}
                    >

                        {paginated.length === 0 ? (

                            <Paper
                                elevation={0}
                                sx={{
                                    py: 10,
                                    textAlign: "center",
                                    borderRadius: "16px",
                                    border: "1px solid #E5E7EB",
                                    bgcolor: "#fff",
                                }}
                            >
                                <ShoppingBagIcon
                                    sx={{
                                        fontSize: 56,
                                        color: "#CBD5E1",
                                    }}
                                />

                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    mt={2}
                                >
                                    No Products Found
                                </Typography>

                                <Typography
                                    color="text.secondary"
                                    fontSize={14}
                                    mt={1}
                                >
                                    Try another category or search keyword.
                                </Typography>

                            </Paper>

                        ) : (

                            <Grid
                                container
                                spacing={3}
                            >

                                {paginated.map((p) => {

                                    const trackedHref =
                                        p.click_token
                                            ? InfluencerStorefrontService.clickUrl(
                                                p.click_token
                                            )
                                            : p.product_url;

                                    return (

                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                            md={4}
                                            lg={3}
                                            xl={3}
                                            key={p.product_id}
                                        >

                                            <Card
                                                elevation={0}
                                                sx={{
                                                    borderRadius: "16px",
                                                    overflow: "hidden",
                                                    border: "1px solid #E5E7EB",
                                                    boxShadow: "0 1px 3px rgba(15,23,42,.06)",
                                                    transition: ".2s",

                                                    "&:hover": {
                                                        transform:
                                                            "translateY(-4px)",
                                                        boxShadow:
                                                            "0 12px 24px rgba(15,23,42,.10)",
                                                    },
                                                }}
                                            >

                                                {/* IMAGE */}

                                                <Box
                                                    sx={{
                                                        position: "relative",
                                                        overflow: "hidden",
                                                    }}
                                                >

                                                    <CardMedia
                                                        component="img"
                                                        image={
                                                            p.primary_image ||
                                                            "https://via.placeholder.com/600x600?text=No+Image"
                                                        }
                                                        alt={p.product_title}
                                                        sx={{
                                                            height: 300,
                                                            objectFit: "cover",
                                                            cursor: "pointer",
                                                            transition: ".3s",
                                                            borderRadius: '0px',
                                                            border: 'none',

                                                            "&:hover": {
                                                                transform:
                                                                    "scale(1.05)",
                                                            },
                                                        }}
                                                        onClick={() =>
                                                            window.open(
                                                                trackedHref,
                                                                "_blank",
                                                                "noopener"
                                                            )
                                                        }
                                                    />

                                                    {p.discount_percent > 0 && (

                                                        <Chip
                                                            label={`${Math.round(
                                                                p.discount_percent
                                                            )}% OFF`}
                                                            sx={{
                                                                position:
                                                                    "absolute",
                                                                top: 12,
                                                                left: 12,
                                                                height: 26,
                                                                fontSize: 12,
                                                                fontWeight: 700,
                                                                borderRadius: "999px",
                                                                bgcolor: "#DC2626",
                                                                color: "#fff",
                                                            }}
                                                        />

                                                    )}

                                                </Box>

                                                {/* CONTENT */}

                                                <CardContent sx={{ p: 2.5 }}>

                                                    {p.category && (

                                                        <Chip
                                                            label={p.category}
                                                            size="small"
                                                            sx={{
                                                                mb: 1.5,
                                                                height: 24,
                                                                fontSize: 12,
                                                                bgcolor:
                                                                    "#EEF2FF",
                                                                color:
                                                                    "#4F46E5",
                                                                fontWeight: 600,
                                                            }}
                                                        />

                                                    )}

                                                    <Typography
                                                        fontWeight={700}
                                                        fontSize={16}
                                                        noWrap
                                                        title={p.product_title}
                                                    >
                                                        {p.product_title}
                                                    </Typography>

                                                    <Box
                                                        mt={1.5}
                                                        display="flex"
                                                        alignItems="center"
                                                        gap={1}
                                                        flexWrap="wrap"
                                                    >

                                                        {p.discount_price ? (
                                                            <>

                                                                <Typography
                                                                    fontWeight={800}
                                                                    fontSize={18}
                                                                    sx={{ color: "#0F172A" }}
                                                                >
                                                                    {sym(
                                                                        p.currency
                                                                    )}
                                                                    {Number(
                                                                        p.discount_price
                                                                    ).toFixed(
                                                                        2
                                                                    )}
                                                                </Typography>

                                                                <Typography
                                                                    fontSize={13}
                                                                    sx={{
                                                                        textDecoration:
                                                                            "line-through",
                                                                        color:
                                                                            "#94A3B8",
                                                                    }}
                                                                >
                                                                    {sym(
                                                                        p.currency
                                                                    )}
                                                                    {Number(
                                                                        p.price
                                                                    ).toFixed(
                                                                        2
                                                                    )}
                                                                </Typography>

                                                            </>
                                                        ) : (

                                                            <Typography
                                                                fontWeight={800}
                                                                fontSize={18}
                                                                sx={{ color: "#0F172A" }}
                                                            >
                                                                {sym(
                                                                    p.currency
                                                                )}
                                                                {Number(
                                                                    p.price
                                                                ).toFixed(
                                                                    2
                                                                )}
                                                            </Typography>

                                                        )}

                                                    </Box>

                                                </CardContent>

                                                <CardActions
                                                    sx={{
                                                        p: 2.5,
                                                        pt: 0,
                                                    }}
                                                >

                                                    <Button
                                                        fullWidth
                                                        size="large"
                                                        variant="contained"
                                                        href={trackedHref}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        startIcon={
                                                            <ShoppingBagIcon />
                                                        }
                                                        sx={{
                                                            borderRadius: "10px",
                                                            height: 44,
                                                            fontWeight: 600,
                                                            textTransform:
                                                                "none",
                                                            bgcolor: "#000",
                                                            boxShadow: "none",

                                                            "&:hover": {
                                                                color: '#fff',
                                                                bgcolor: "#111",
                                                                boxShadow: "none",
                                                            },
                                                        }}
                                                    >
                                                        Shop Now
                                                    </Button>

                                                </CardActions>

                                            </Card>

                                        </Grid>

                                    );
                                })}

                            </Grid>

                        )}

                        {filtered.length > PAGE_SIZE && (

                            <Box
                                display="flex"
                                justifyContent="center"
                                mt={6}
                            >

                                <Pagination
                                    page={page}
                                    count={Math.ceil(
                                        filtered.length /
                                        PAGE_SIZE
                                    )}
                                    color="primary"
                                    size="large"
                                    shape="rounded"
                                    onChange={(_, value) => {
                                        setPage(value);
                                        window.scrollTo({
                                            top: 0,
                                            behavior: "smooth",
                                        });
                                    }}
                                />

                            </Box>

                        )}
                    </Box>
                    <Box
                        py={4}
                        textAlign="center"
                        mt={4}
                        sx={{ borderTop: "1px solid #E5E7EB" }}
                    >
                        <Typography
                            variant="body2"
                            fontSize={13}
                            color="text.secondary"
                        >
                            Powered by <b>ClanConnect</b>
                        </Typography>
                    </Box>

                </Box>
            </Box>




            {/* <Box sx={{ minHeight: '100vh', bgcolor: '#FAFAFA' }}>

            {storefront.store_banner && (
                <Box sx={{ height: 800, overflow: 'hidden' }}>
                    <img src={storefront.store_banner} alt="banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </Box>
            )}  

            <Box px={{ xs: 2, md: 4 }} py={3} display="flex" alignItems="center" gap={2} sx={{ borderBottom: '1px solid #eee', bgcolor: '#fff' }}>
                <Avatar src={storefront.store_logo} sx={{ width: 64, height: 64 }} variant="rounded" />
                <Box>
                    <Typography variant="h5" fontWeight={700}>{storefront.store_name}</Typography>
                    {storefront.store_description && (
                        <Typography variant="body2" color="text.secondary" mt={0.5}>{storefront.store_description}</Typography>
                    )}
                </Box>
            </Box>

            <Box px={{ xs: 2, md: 4 }} py={3}>
                <Box display="flex" gap={2} mb={3} flexWrap="wrap">
                    <TextField size="small" label="Search products" value={search}
                        onChange={e => setSearch(e.target.value)} sx={{ width: 240, bgcolor: '#fff' }} />
                    {categories.length > 0 && (
                        <FormControl size="small" sx={{ width: 180, bgcolor: '#fff' }}>
                            <InputLabel>Category</InputLabel>
                            <Select value={category} label="Category" onChange={e => setCategory(e.target.value)}>
                                <MenuItem value="">All</MenuItem>
                                {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>
                    )}
                    <Chip label={`${filtered.length} products`} variant="outlined" />
                </Box>

                {paginated.length === 0 ? (
                    <Alert severity="info">No products found</Alert>
                ) : (
                    <Grid container spacing={3}>
                        {paginated.map(p => {
                            const trackedHref = p.click_token ? InfluencerStorefrontService.clickUrl(p.click_token) : p.product_url;
                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={p.product_id}>
                                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                        <CardMedia component="img" height={200}
                                            image={p.primary_image || 'https://via.placeholder.com/300x200?text=No+Image'}
                                            alt={p.product_title}
                                            // inline style overrides the app-wide `img { border-radius: 50% }` that otherwise ovals the image
                                            style={{ objectFit: 'cover', width: '100%', height: 200, borderRadius: 0, cursor: 'pointer' }}
                                            onClick={() => window.open(trackedHref, '_blank', 'noopener')} />
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Typography variant="subtitle2" fontWeight={600} noWrap title={p.product_title}>{p.product_title}</Typography>
                                            {p.category && <Chip label={p.category} size="small" sx={{ mt: 0.5, mb: 0.5 }} />}
                                            <Box display="flex" alignItems="center" gap={1} mt={1}>
                                                {p.discount_price ? (
                                                    <>
                                                        <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                                            {sym(p.currency)}{parseFloat(p.price).toFixed(2)}
                                                        </Typography>
                                                        <Typography variant="subtitle1" color="primary" fontWeight={700}>
                                                            {sym(p.currency)}{parseFloat(p.discount_price).toFixed(2)}
                                                        </Typography>
                                                        {p.discount_percent && <Chip size="small" label={`${Math.round(p.discount_percent)}% off`} color="success" />}
                                                    </>
                                                ) : (
                                                    <Typography variant="subtitle1" fontWeight={700}>{sym(p.currency)}{parseFloat(p.price).toFixed(2)}</Typography>
                                                )}
                                            </Box>
                                        </CardContent>
                                        <CardActions sx={{ px: 2, pb: 2 }}>
                                            <Button fullWidth variant="contained" startIcon={<ShoppingBagIcon />}
                                                href={trackedHref} target="_blank" rel="noopener noreferrer">
                                                Shop now
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}

                {filtered.length > PAGE_SIZE && (
                    <Box display="flex" justifyContent="center" mt={4}>
                        <Pagination count={Math.ceil(filtered.length / PAGE_SIZE)} page={page}
                            onChange={(_, v) => { setPage(v); window.scrollTo(0, 0); }} color="primary" />
                    </Box>
                )}

                <Box textAlign="center" mt={6} pb={3}>
                    <Typography variant="caption" color="text.secondary">Powered by ClanConnect</Typography>
                </Box>
            </Box>
        </Box> */}
        </>
    );
};
